import { type IHydratedWidget } from '@framework/engine/hydrate/IHydrationContext.ts';
import { generateMarginStyle } from '@framework/engine/schema/directives/margin/generateMarginStyle.ts';
import { type IHtmlRenderContext } from '@framework/engine/schema/widgets/IWidgetType.ts';
import { type IImageWidget, type ImageAlignment } from './IImageWidget.ts';

const VALID_ALIGNMENTS: ImageAlignment[] = ['left', 'center', 'right'];

function normalizeAlignment(value: unknown): ImageAlignment {
  return VALID_ALIGNMENTS.includes(value as ImageAlignment)
    ? (value as ImageAlignment)
    : 'left';
}

const VALID_CONTENT_TYPES: string[] = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/avif',
];

function normalizeContentType(value: unknown): string {
  return typeof value === 'string' && VALID_CONTENT_TYPES.includes(value)
    ? value
    : 'image/png';
}

function normalizeDimension(value: unknown, fallback: string): string {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return `${Math.round(value)}px`;
  }
  return fallback;
}

const BASE64_PATTERN = /^[A-Za-z0-9+/]*={0,2}$/;

function normalizeBase64(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const stripped = value.replace(/\s+/g, '');
  return BASE64_PATTERN.test(stripped) ? stripped : null;
}

export function renderImageHtml(
  widget: IHydratedWidget<IImageWidget>,
  context: IHtmlRenderContext,
): string {
  if (!widget.imageId) return '';
  if (!context.images?.[widget.imageId]) return '';

  const marginStyle = generateMarginStyle(widget.directives?.margin);

  const img = context.images[widget.imageId];

  const base64 = normalizeBase64(img.base64);
  if (base64 === null) return '';

  const contentType = normalizeContentType(img.contentType);
  const src = `data:${contentType};base64,${base64}`;

  // Styling
  const maxWidth = normalizeDimension(widget.maxWidth, '100%');
  const maxHeight = normalizeDimension(widget.maxHeight, 'auto');
  const alignment = normalizeAlignment(widget.alignment);

  return `<div style="text-align:${alignment};${marginStyle}"><img src="${src}" style="max-width:${maxWidth};max-height:${maxHeight}" /></div>`;
}
